/**
 * @fileoverview パスワード変更画面のコンポーネント
 *
 * このファイルでは、ログインユーザーがパスワードを変更するためのフォームを提供します。
 * 現在のパスワードと新しいパスワードを入力し、検証した上でパスワード変更を実行します。
 */

/**
 * パスワード変更フォームコンポーネント
 *
 * 現在のパスワード、新しいパスワード、パスワード確認の入力フィールドを提供し、
 * 入力値の検証とパスワード変更APIとの連携を行います。
 * バリデーションエラーやAPI通信エラーのハンドリングも含みます。
 *
 * @returns {JSX.Element} パスワード変更フォーム
 */

import { useState, useContext, useEffect } from "react";
import { Stack, PasswordInput, Text } from "@mantine/core";
import { useForm, matchesField, isNotEmpty } from "@mantine/form";
import { useMutation } from "@tanstack/react-query";

import { useNavigate } from "@/router";
import { Card } from "@/components/card";
import { Button } from "@/components/button";
import { updatePassword } from "@/api/updatePassword";
import { CsrfTokenContext } from "@/providers/csrfToken/context";

export default function ChagnePasswordPage() {
  const passwordValidator = (value: string) => {
    if (/\s/.test(value)) {
      return "パスワードに空白を含めることはできません。";
    }
    if (value.length < 12) {
      return "パスワードは12文字以上である必要があります。";
    }
    if (value.length > 128) {
      return "パスワードは128文字以下である必要があります。";
    }
    return null;
  };
  const form = useForm({
    mode: "uncontrolled",
    initialValues: {
      currentPassword: "",
      newPassword: "",
      confirmNewPassword: "",
    },
    validate: {
      currentPassword: isNotEmpty("現在のパスワードを入力してください。"),
      newPassword: passwordValidator,
      confirmNewPassword: matchesField(
        "newPassword",
        "新規パスワードが一致しません。"
      ),
    },
    validateInputOnChange: ["newPassword"],
    validateInputOnBlur: ["currentPassword", "confirmNewPassword"],
  });

  const csrfTokenContext = useContext(CsrfTokenContext);

  const navigate = useNavigate();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const messagePasswordError = (message: string) => {
    setErrorMessage(message);
  };

  const { mutate, isPending, isSuccess, isError } = useMutation({
    mutationKey: ["updatePassword"],
    mutationFn: async (args: {
      currentPassword: string;
      newPassword: string;
      csrfToken: string;
    }) =>
      await updatePassword(
        args.currentPassword,
        args.newPassword,
        args.csrfToken
      ),
  });

  useEffect(() => {
    if (isSuccess) {
      navigate("/change-password/success");
    }
  }, [isSuccess]);

  useEffect(() => {
    if (isError) {
      navigate("/change-password/failure");
    }
  }, [isError]);

  const handleChangePassword = async (values: typeof form.values) => {
    if (values.newPassword !== values.confirmNewPassword) {
      messagePasswordError("新規パスワードが一致しません。");
      return;
    }

    if (!csrfTokenContext.csrfToken) {
      console.error("CSRF token is not found.");
      setErrorMessage("CSRF token is not found.");
      return;
    }

    mutate({
      currentPassword: values.currentPassword,
      newPassword: values.newPassword,
      csrfToken: csrfTokenContext.csrfToken,
    });
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <Card
      title="パスワード変更"
      description="現在のパスワードと新たに設定するパスワードを入力してください。"
    >
      <form onSubmit={form.onSubmit(handleChangePassword)}>
        <Stack className="gap-10">
          <div className="flex flex-col gap-5">
            <PasswordInput
              key={form.key("currentPassword")}
              {...form.getInputProps("currentPassword")}
              label="現在のパスワード"
              placeholder="current password"
              required={true}
            />
            <PasswordInput
              key={form.key("newPassword")}
              {...form.getInputProps("newPassword")}
              label="新規パスワード"
              placeholder="new password"
              required={true}
            />
            <PasswordInput
              key={form.key("confirmNewPassword")}
              {...form.getInputProps("confirmNewPassword")}
              label="新規パスワードの確認"
              placeholder="new password"
              required={true}
            />
          </div>
          {errorMessage && (
            <Text c="red" fw={700}>
              {errorMessage}
            </Text>
          )}
          <div className="flex justify-between">
            <Button text="戻る" onClick={handleBack} disabled={isPending} />
            <Button
              text="変更"
              type="submit"
              disabled={isPending}
              loading={isPending}
            />
          </div>
        </Stack>
      </form>
    </Card>
  );
}
